const BASE = '/server/lender_declaration_function/api';
const OPS_TOKEN_KEY = 'fair_ops_token';

export function getOpsToken() {
  return localStorage.getItem(OPS_TOKEN_KEY);
}
export function setOpsToken(t) {
  if (t) localStorage.setItem(OPS_TOKEN_KEY, t);
  else localStorage.removeItem(OPS_TOKEN_KEY);
}

async function request(path, { method = 'GET', body, ops = false, raw = false } = {}) {
  const headers = { 'Content-Type': 'application/json' };
  if (ops) {
    const tok = getOpsToken();
    if (tok) headers['X-App-Token'] = tok;
  }
  const init = { method, headers };
  if (body !== undefined) init.body = JSON.stringify(body);

  const res = await fetch(`${BASE}${path}`, init);
  if (raw) return res;
  let data = null;
  const text = await res.text();
  try { data = text ? JSON.parse(text) : null; } catch { data = text; }
  if (!res.ok) {
    const err = new Error((data && data.error) || `request_failed_${res.status}`);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

function clientQs(auth) {
  return `id=${encodeURIComponent(auth.id)}&t=${encodeURIComponent(auth.token)}`;
}

export const clientApi = {
  init: (auth) => request(`/client/init?${clientQs(auth)}`),
  markSent: (auth, lenderName) => request(`/client/markSent?${clientQs(auth)}`, { method: 'POST', body: { lenderName } }),
  markUnsent: (auth, lenderName) => request(`/client/markUnsent?${clientQs(auth)}`, { method: 'POST', body: { lenderName } }),
  reportProblem: (auth, lenderName, reason) => request(`/client/reportProblem?${clientQs(auth)}`, { method: 'POST', body: { lenderName, reason } }),
};

export const adminAuthApi = {
  login: (username, password) => request('/admin/auth/login', { method: 'POST', body: { username, password } }),
  me: () => request('/admin/auth/me', { ops: true }),
};

// `new URLSearchParams({a: undefined})` produces `a=undefined` (literal
// string), not an empty/missing param. Strip null/undefined/empty values
// before serialising — otherwise the backend sees e.g. `stage=undefined`
// and treats it as a real filter, returning zero rows. Caused the contacts
// page to show "No contacts match" for weeks.
function toQs(params) {
  const clean = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
  );
  return new URLSearchParams(clean).toString();
}

export const adminApi = {
  metrics: (params = {}) => {
    const qs = toQs(params);
    return request(`/admin/metrics${qs ? `?${qs}` : ''}`, { ops: true });
  },
  contacts: (params = {}) => {
    const qs = toQs(params);
    return request(`/admin/contacts${qs ? `?${qs}` : ''}`, { ops: true });
  },
  contactDetail: (id) => request(`/admin/contacts/${encodeURIComponent(id)}`, { ops: true }),
  revealEmail: (id) => request(`/admin/contacts/${encodeURIComponent(id)}/revealEmail`, { method: 'POST', ops: true }),
  resendNotification: (contactId, regenerateToken = false) => request('/admin/resendNotification', { method: 'POST', body: { contactId, regenerateToken }, ops: true }),
  bulkResend: (scope = 'not_started', { contactIds, dryRun = true, confirm = false, pdfSentDate } = {}) =>
    request('/admin/bulkResend', { method: 'POST', body: { scope, contactIds, dryRun, confirm, pdfSentDate }, ops: true }),
  regenerateToken: (contactId) => request('/admin/regenerateToken', { method: 'POST', body: { contactId }, ops: true }),
  markLenderSent: (contactId, lenderName, reason) => request('/admin/markLenderSent', { method: 'POST', body: { contactId, lenderName, reason }, ops: true }),
  forceComplete: (contactId, reason) => request('/admin/forceComplete', { method: 'POST', body: { contactId, reason }, ops: true }),
  exportChaseList: async (stage) => {
    const res = await request('/admin/exportChaseList', { method: 'POST', body: { stage: stage || undefined }, ops: true, raw: true });
    if (!res.ok) throw new Error(`export_failed_${res.status}`);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    return url;
  },
  quarantine: () => request('/admin/quarantine', { ops: true }),
  resolveQuarantine: (bccId, action, contactId) => request(`/admin/quarantine/${encodeURIComponent(bccId)}/resolve`, { method: 'POST', body: { action, contactId }, ops: true }),
  listUsers: () => request('/admin/users', { ops: true }),
  createUser: (payload) => request('/admin/users', { method: 'POST', body: payload, ops: true }),
  disableUser: (userId) => request(`/admin/users/${encodeURIComponent(userId)}/disable`, { method: 'POST', ops: true }),
  resetPassword: (userId, password) => request(`/admin/users/${encodeURIComponent(userId)}/resetPassword`, { method: 'POST', body: { password }, ops: true }),
  getConfig: () => request('/admin/config', { ops: true }),
  saveConfig: (entries) => request('/admin/config', { method: 'POST', body: { entries }, ops: true }),
};
